// How a queued notification is NAMED on a staff screen.
//
// 🔴 F138 kolečko 2. Two panes render the same rows and they disagreed. The
// Servis queue (wave 4) had a presenter; the client detail's own notification
// pane printed `String(item.type)` and `String(item.status)` straight into the
// card, so the flattening wave lifted "deliverable_partner_review / sent" and
// "client_step_unlocked / failed" out of a tab and onto the primary surface.
//
// Worse, the presenter it should have been sharing had drifted on its own:
// `NOTIFICATION_TYPE_NAMES` listed eight names of which SEVEN (login_link,
// welcome, call_reminder, material_review, material_published, feedback_reply,
// staff_review) match no notification this product has ever queued. So the
// dictionary looked complete and answered almost nothing.
//
// One dictionary now, and it is not hand-typed: the types come from
// `shared/mail-catalog.ts`, which F117 made the inventory of every
// notification the product sends. A mail added there without a name fails
// `tests/aktivita-f138.test.ts` in both locales.
import { NOTIFICATION_TYPES } from "../../shared/mail-catalog";

export type NotificationStatus = "queued" | "dev_logged" | "sent" | "failed" | "skipped";

/** The statuses the `notifications` CHECK allows (migration 0007). */
export const NOTIFICATION_STATUSES: readonly NotificationStatus[] = [
  "queued", "dev_logged", "sent", "failed", "skipped"
];

/** Every type a row can carry, from the F117 inventory. */
export const NOTIFICATION_TYPE_NAMES: readonly string[] = NOTIFICATION_TYPES;

/**
 * A type nobody named still must not print itself. An old row, or a type
 * queued by a build newer than this bundle, says what it is in words; the
 * internal string belongs to the technical fold, not to a card heading
 * (směr, princip 5).
 */
export function notificationTypeLabel(type: string, t: (key: string) => string): string {
  return NOTIFICATION_TYPE_NAMES.includes(type)
    ? t(`staffConsole.adminNotifications.typeName.${type}`)
    : t("staffConsole.adminNotifications.typeUnknown");
}

/**
 * 🔴 Naming an unknown type must not LOSE it. Wave 2 shipped the raw string on
 * purpose ("never guessed away") and it had a point: the type is nowhere else
 * in Servis, so an admin debugging a message nobody named would have nothing
 * left to search for. So the internal string stays REACHABLE and stops being
 * the label - the same idiom this module already uses for a push recipient's
 * user id, and the same split the client history makes by putting the type in
 * the admin-only technical fold.
 *
 * A named type returns undefined: a tooltip repeating the label is noise.
 */
export function notificationTypeHint(type: string): string | undefined {
  return NOTIFICATION_TYPE_NAMES.includes(type) ? undefined : type;
}

/**
 * The row's headline: the subject a human would recognise, else the type's
 * human name. Same rule on both surfaces, so one client cannot be described
 * two ways on two screens.
 */
export function notificationTitle(
  item: { subject?: string | null; type: string },
  t: (key: string) => string
): string {
  return item.subject || notificationTypeLabel(item.type, t);
}

/** Czech (or English) for a status. An unknown one is named, never printed. */
export function notificationStatusLabel(status: string, t: (key: string) => string): string {
  return (NOTIFICATION_STATUSES as readonly string[]).includes(status)
    ? t(`staffConsole.adminNotifications.status.${status}`)
    : t("staffConsole.adminNotifications.statusUnknown");
}

/**
 * Failure reads red, waiting reads amber, delivered reads green.
 *
 * 🔴 The development sink is NOT delivered (S3-09): `dev_logged` wore the same
 * green as "Odesláno", so thousands of messages that never left the building
 * sat in the colour of success in the one panel built to catch a message that
 * quietly went nowhere. A sink is neither a success nor a failure, so it
 * carries no verdict colour at all.
 */
export function notificationStatusTone(status: string): "bad" | "wait" | "good" | "muted" {
  if (status === "failed") return "bad";
  if (status === "queued") return "wait";
  if (status === "sent") return "good";
  return "muted";
}

// ---- One card per MESSAGE (F138 kolečko 3, fault 1) ----

/**
 * 🔴 The wall was the TRANSPORT drawn as content.
 *
 * `queueNotification` (worker/src/notify.ts) turns one message into one row
 * PER CHANNEL and PER RECIPIENT, each carrying the same `command_id` and the
 * same `type`. So a message sent by e-mail and push is two rows, and the same
 * message to two partner addresses is two more: the six cards the verdict
 * counted under "Něco čeká na tým" are three messages. Nothing was duplicated
 * in the data; the pane was printing the delivery mechanism.
 *
 * The grouping key is therefore the command id, which is exactly the identity
 * the queue itself uses - the dedup key is built from it and the recipient, so
 * two rows sharing a command id are by construction the same message going to
 * different places. A row with no command id (nothing writes one today, but an
 * older row can) stands alone rather than being folded into a stranger.
 */
export type NotificationChannelName = "email" | "push" | "other";

export interface NotificationRowLike {
  id: string;
  type: string;
  status: string;
  channel?: string | null;
  subject?: string | null;
  recipient?: string | null;
  recipientName?: string | null;
  recipientEmail?: string | null;
  commandId?: string | null;
  createdAt?: string | null;
  attempts?: unknown;
  attemptCount?: number | null;
}

export interface NotificationDelivery {
  channel: NotificationChannelName;
  status: string;
  rows: NotificationRowLike[];
}

export interface NotificationMessage {
  key: string;
  type: string;
  subject: string | null;
  createdAt: string | null;
  deliveries: NotificationDelivery[];
  /** Human labels, in first-seen order, never a raw id. */
  recipients: string[];
  /** Ids we could not turn into a person; reachable, never the label. */
  unresolvedRecipients: string[];
  attempts: number;
  /** The rows a retry would act on - the failed ones, in row order. */
  failedRows: NotificationRowLike[];
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A channel value the row does not carry reads as e-mail: the magic-link
 * inserts predate the column and only ever went by mail. */
export function notificationChannel(value: string | null | undefined): NotificationChannelName {
  if (value === "push") return "push";
  if (value === "email" || !value) return "email";
  return "other";
}

/**
 * 🔴 A person, never an id. The push row addresses a USER, so its `recipient`
 * is a uuid; the server resolves it through `users` and this picks the first
 * thing a human would recognise. An id we could not resolve does NOT become
 * the label - it stays reachable (the idiom `notificationTypeHint` already
 * uses for an unnamed type), because a raw uuid on a partner's screen is an
 * internal code (směr, princip 5).
 */
export function notificationRecipientLabel(row: NotificationRowLike): string | null {
  const name = typeof row.recipientName === "string" ? row.recipientName.trim() : "";
  if (name) return name;
  const email = typeof row.recipientEmail === "string" ? row.recipientEmail.trim() : "";
  if (email) return email;
  const raw = typeof row.recipient === "string" ? row.recipient.trim() : "";
  if (raw && !UUID.test(raw)) return raw;
  return null;
}

/** The one worst state of a set: a failure outranks a wait, a wait a success. */
function worstStatus(rows: NotificationRowLike[]): string {
  const order = ["failed", "queued", "sent", "dev_logged", "skipped"];
  let best = rows[0]?.status ?? "queued";
  for (const row of rows) {
    if (order.indexOf(row.status) < order.indexOf(best)) best = row.status;
  }
  return best;
}

export function groupNotifications(rows: readonly NotificationRowLike[]): NotificationMessage[] {
  const messages = new Map<string, NotificationMessage>();
  for (const row of rows) {
    const command = typeof row.commandId === "string" && row.commandId ? row.commandId : null;
    const key = command ? `${row.type}:${command}` : `row:${row.id}`;
    let message = messages.get(key);
    if (!message) {
      message = {
        key,
        type: row.type,
        subject: typeof row.subject === "string" && row.subject ? row.subject : null,
        createdAt: typeof row.createdAt === "string" ? row.createdAt : null,
        deliveries: [],
        recipients: [],
        unresolvedRecipients: [],
        attempts: 0,
        failedRows: []
      };
      messages.set(key, message);
    }
    // The subject is a property of the message, so the first row that carries
    // one speaks for all of them (a push row stores no subject).
    if (!message.subject && typeof row.subject === "string" && row.subject) message.subject = row.subject;
    const channel = notificationChannel(row.channel);
    const delivery = message.deliveries.find((entry) => entry.channel === channel);
    if (delivery) delivery.rows.push(row);
    else message.deliveries.push({ channel, status: row.status, rows: [row] });
    const label = notificationRecipientLabel(row);
    if (label && !message.recipients.includes(label)) message.recipients.push(label);
    if (!label && typeof row.recipient === "string" && row.recipient
      && !message.unresolvedRecipients.includes(row.recipient)) {
      message.unresolvedRecipients.push(row.recipient);
    }
    const attempts = Array.isArray(row.attempts)
      ? row.attempts.length
      : Number(row.attemptCount ?? 0);
    message.attempts += Number.isFinite(attempts) ? attempts : 0;
    if (row.status === "failed") message.failedRows.push(row);
  }
  for (const message of messages.values()) {
    for (const delivery of message.deliveries) delivery.status = worstStatus(delivery.rows);
  }
  return [...messages.values()];
}

/**
 * "Pokusy" is a diagnostic, and a diagnostic printed under every healthy row is
 * noise: 39 cards each saying "Pokusy: 1" told an admin nothing about the one
 * message that failed. It speaks when there is something to say - a retry
 * happened, or something failed.
 */
export function notificationShowsAttempts(message: NotificationMessage): boolean {
  return message.attempts > message.deliveries.reduce((sum, entry) => sum + entry.rows.length, 0)
    || message.failedRows.length > 0;
}

/** Newest eight stay open; the rest fold, the idiom the history already uses. */
export const NOTIFICATION_VISIBLE_MAX = 8;
